import { Button, FormLayout, GridFieldGroup, Heading, Paragraph, TextInput, useDialog } from '@expressable/syntax-ds';
import { DirectSignupNoPCPInfoRequiredDialog } from 'components/Dialog/DirectSignupNoPCPInfoDialog';
import { useSignupDirectCtx } from 'domains/signupDirect/context';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import { EvaluationFormType } from 'domains/signupDirect/schemas/evaluationSchema';
import { HeadingCombo } from 'domains/signupDirect/SyntaxComps/HeadingCombo';
import React, { useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTrack } from 'utils/hooks/useMixPanel';
import { canShowPCPSection } from './canShowPCPSection';
import { JotformRedirectReason } from 'domains/signupDirect/misc/jotform/urls';

const DirectSignupPCPSection: React.FC = () => {
  const { track } = useTrack();
  const { t } = useTranslation();
  const { register } = useFormContext<EvaluationFormType>();
  const { isResumedLead, onContinueToJotformRegistrationForm } = useSignupDirectCtx();
  const { dialogStateProps, onOpen, onClose: onCloseDialog } = useDialog();

  const onBailoutNoPPIDialog = useCallback(() => {
    track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.FORM.NO_PCP_BAILOUT, { isResumedLead });
    onContinueToJotformRegistrationForm(JotformRedirectReason.NO_PCP_BAILOUT);

    onCloseDialog();
  }, []);

  const isPCPRequired = useWatch<EvaluationFormType, 'primaryPhysician.physicianOrderRequired'>({
    name: 'primaryPhysician.physicianOrderRequired',
  });

  const eligibilityCheckResult = useWatch<EvaluationFormType, 'billingInfo.eligibilityCheckResult'>({
    name: 'billingInfo.eligibilityCheckResult',
  });

  const insuranceType = useWatch<EvaluationFormType, 'billingInfo.insuranceData.insuranceType'>({
    name: 'billingInfo.insuranceData.insuranceType',
  });

  const shouldShowPCPSection = canShowPCPSection(
    insuranceType,
    isPCPRequired as unknown as boolean,
    eligibilityCheckResult,
  );

  if (!shouldShowPCPSection) return null;

  return (
    <>
      <FormLayout.Fields className="mobile-xs-d:gap-e24 tablet-d:gap-e32">
        <HeadingCombo>
          <Heading noDefaultMargins level="h5">
            {t('directSignup.sections.primaryPhysician.title')}
          </Heading>
          <Paragraph variant="secondary" size="md">
            {t('directSignup.sections.primaryPhysician.subTitle')}
          </Paragraph>
        </HeadingCombo>

        <TextInput
          data-testid="primary-physician-name-input"
          label={t('directSignup.sections.primaryPhysician.labels.physicianName')}
          {...register('primaryPhysician.name')}
        />
        <TextInput
          data-testid="primary-physician-office-name-input"
          label={t('directSignup.sections.primaryPhysician.labels.physicianOfficeName')}
          hint={t('directSignup.sections.primaryPhysician.labels.physicianOfficeNameHint')}
          {...register('primaryPhysician.officeName')}
        />

        <GridFieldGroup>
          <TextInput
            data-testid="primary-physician-phone-input"
            label={t('directSignup.sections.primaryPhysician.labels.physicianPhone')}
            type="tel"
            {...register('primaryPhysician.phone')}
          />
          <TextInput
            data-testid="primary-physician-email-input"
            label={t('directSignup.sections.primaryPhysician.labels.physicianWebsite')}
            type="url"
            {...register('primaryPhysician.website')}
          />
        </GridFieldGroup>

        <Button data-testid="no-pcp-button" variant="link" onClick={onOpen}>
          {t('directSignup.sections.primaryPhysician.labels.noPhysicianInfo')}
        </Button>
      </FormLayout.Fields>

      <DirectSignupNoPCPInfoRequiredDialog {...dialogStateProps} onBailout={onBailoutNoPPIDialog} />
    </>
  );
};

export default DirectSignupPCPSection;
